import { BaseService } from './BaseService';
import { Transaction, TransactionEntry } from '../types/accounting';
import { ServiceResponse } from '../types/core';
import { DatabaseError, ValidationError } from '../utils/errors';
import { Pool, QueryResult } from 'pg';
import { ITransaction, TransactionContext } from '../types/transaction';
import apiInstance from './api';
import { API_ENDPOINTS } from '../config/api';
import { AxiosResponse } from 'axios';

interface TransactionQueryResult extends QueryResult {
  rows: Transaction[];
}

interface TransactionDocument {
  id: string;
  transaction_id: string;
  document_type: string;
  filename: string;
  mime_type: string;
  size: number;
  metadata: Record<string, any>;
  created_at: Date;
}

interface TransactionAuditLog {
  id: string;
  transaction_id: string;
  action: string;
  user_id: string;
  details: Record<string, any>;
  created_at: Date;
}

interface DocumentQueryResult extends QueryResult {
  rows: TransactionDocument[];
}

interface AuditLogQueryResult extends QueryResult {
  rows: TransactionAuditLog[];
}

interface FilterQueryResult extends QueryResult {
  rows: { 
    status?: string;
    type?: string;
    category?: string;
  }[];
}

interface TransactionSummary {
  total_count: number;
  total_amount: number;
  pending_count: number;
  pending_amount: number;
}

interface MonthlyTrend {
  month: Date;
  count: number;
  amount: number;
}

interface TransactionTrends {
  monthly: MonthlyTrend[];
}

interface SummaryQueryResult extends QueryResult {
  rows: TransactionSummary[];
}

interface TrendsQueryResult extends QueryResult {
  rows: MonthlyTrend[];
}

export interface IJournalEntry {
  id?: string;
  date: string;
  description: string;
  amount: number;
  type: string;
  fiscalSponsorId: string;
  fiscalCategory?: {
    cause?: string;
    campaign?: string;
    fundType?: string;
    restrictionType?: string;
  };
  accountCategory?: string;
  subCategory?: string;
  accountCode?: string;
  taxCategory?: string;
  isRecurring?: boolean;
  documents?: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
  }>;
  metadata?: Record<string, any>;
  lineItems: Array<{
    accountId: string;
    description: string;
    debit?: number;
    credit?: number;
    taxCategory?: string;
    fundRestriction?: string;
  }>;
}

export interface JournalEntryFilters {
  type: string;
  category: string;
  search: string;
  showInactive: boolean;
  taxCategory: string;
  fundType: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  status?: string;
  amount?: {
    min: number;
    max: number;
  };
}

export interface RecurringEntryOptions {
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  startDate: Date;
  endDate?: Date;
  dayOfMonth?: number;
  dayOfWeek?: number;
}

export interface ApprovalResult {
  success: boolean;
  message?: string;
  transaction?: ITransaction;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// Define the ApiTransaction interface locally
interface ApiTransaction {
  _id: string;
  date: string;
  type: string;
  description: string;
  amount: number;
  status: string;
  fiscalSponsorId: string;
  userId: string;
  metadata?: Record<string, any>;
}

export class TransactionService extends BaseService {
  // Inherits constructor from BaseService

  private static mapTransaction(transaction: ITransaction | any): ITransaction {
    return {
      id: transaction._id || transaction.id, // Handle both _id and id
      date: transaction.date,
      type: transaction.type,
      description: transaction.description,
      amount: transaction.amount,
      status: transaction.status,
      fiscalSponsorId: transaction.fiscalSponsorId,
      userId: transaction.userId,
      metadata: transaction.metadata
    };
  }

  static async create(data: Partial<IJournalEntry>): Promise<ITransaction> {
    try {
      const response: AxiosResponse<ApiResponse<ITransaction>> = await apiInstance.post('/transactions', {
        ...data,
        type: 'JOURNAL_ENTRY',
        status: 'pending',
        metadata: {
          ...data.metadata,
          entryType: 'manual',
          lineItems: data.lineItems
        }
      });
      return this.mapTransaction(response.data.data);
    } catch (error) {
      console.error('Error creating journal entry:', error);
      throw error;
    }
  }

  static async list(fiscalSponsorId: string): Promise<ITransaction[]> {
    try {
      const response: AxiosResponse<ApiResponse<ITransaction[]>> = await apiInstance.get(`/transactions/${fiscalSponsorId}`);
      return response.data.data;
    } catch (error) {
      throw new Error(`Failed to fetch transactions: ${error}`);
    }
  }

  static async approve(entryId: string, approverId: string): Promise<ApprovalResult> {
    try {
      const response: AxiosResponse<ApiResponse<ITransaction>> = await apiInstance.put(`/transactions/${entryId}/approve`, {
        status: 'completed',
        metadata: {
          approvedBy: approverId,
          approvedAt: new Date().toISOString()
        }
      });
      
      return {
        success: true,
        transaction: this.mapTransaction(response.data.data),
        message: 'Transaction approved successfully'
      };
    } catch (error) {
      console.error('Error approving transaction:', error);
      throw error;
    }
  }

  static async validate(data: Partial<IJournalEntry>): Promise<boolean> {
    try {
      const response: AxiosResponse<ApiResponse<boolean>> = await apiInstance.post('/transactions/validate', {
        amount: data.amount || 0,
        description: data.description || '',
        dateSubmitted: data.date || new Date().toISOString(),
        submittedBy: '',
        approvalPath: '',
        project: '',
        projectMission: '',
        charitablePurpose: '',
        attachments: [],
        submissionToken: ''
      });
      
      return response.data.data;
    } catch (error) {
      console.error('Error validating journal entry:', error);
      return false;
    }
  }

  async get(id: string): Promise<Transaction> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        'SELECT * FROM transactions WHERE id = $1',
        [id]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError(`Transaction with id ${id} not found`);
      }

      return result.rows[0];
    });
  }

  async update(id: string, data: Partial<Transaction>): Promise<Transaction> {
    return await this.withTransaction(async (client) => {
      const updates = Object.keys(data)
        .map((key, index) => `${key} = $${index + 2}`)
        .join(', ');

      const result = await client.query(
        `UPDATE transactions 
         SET updated_at = NOW(), 
             ${updates}
         WHERE id = $1 
         RETURNING *`,
        [id, ...Object.values(data)]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError(`Transaction with id ${id} not found`);
      }

      return result.rows[0];
    });
  }

  async delete(id: string): Promise<void> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        'DELETE FROM transactions WHERE id = $1 RETURNING id',
        [id]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError(`Transaction with id ${id} not found`);
      }
    });
  }

  async approve(id: string, approverId: string): Promise<Transaction> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `UPDATE transactions 
         SET status = 'approved', 
             approved_by = $2,
             approved_at = NOW()
         WHERE id = $1
         RETURNING *`,
        [id, approverId]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError(`Transaction with id ${id} not found`);
      }

      return result.rows[0];
    });
  }

  async reject(id: string, userId: string, reason: string, notes?: string): Promise<Transaction> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `UPDATE transactions 
         SET status = 'rejected',
             rejected_by = $2,
             rejected_at = NOW(),
             rejection_reason = $3,
             rejection_notes = $4
         WHERE id = $1
         RETURNING *`,
        [id, userId, reason, notes]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError(`Transaction with id ${id} not found`);
      }

      return result.rows[0];
    });
  }

  async createReversal(
    id: string,
    reason: string,
    notes?: string,
    date: Date = new Date()
  ): Promise<Transaction> {
    return await this.withTransaction(async (client) => {
      // Get original transaction
      const originalResult = await client.query(
        'SELECT * FROM transactions WHERE id = $1',
        [id]
      ) as TransactionQueryResult;

      const original = originalResult.rows[0];
      if (!original) {
        throw new DatabaseError(`Transaction with id ${id} not found`);
      }

      // Create reversal entries
      const result = await client.query(
        `INSERT INTO transactions (
           fiscal_sponsor_id,
           type,
           status,
           amount,
           date,
           reversal_for,
           reason,
           notes
         ) VALUES ($1, 'reversal', 'completed', $2, $3, $4, $5, $6)
         RETURNING *`,
        [
          original.fiscalSponsorId,
          -original.amount,
          date,
          id,
          reason,
          notes
        ]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError('Failed to create reversal transaction');
      }

      return result.rows[0];
    });
  }

  async batchApprove(
    transactionIds: string[],
    approverId: string,
    notes?: string
  ): Promise<Transaction[]> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `UPDATE transactions 
         SET status = 'approved',
             approved_by = $2,
             approved_at = NOW(),
             approval_notes = $3
         WHERE id = ANY($1)
         RETURNING *`,
        [transactionIds, approverId, notes]
      ) as TransactionQueryResult;

      if (result.rows.length === 0) {
        throw new DatabaseError('No transactions found with the provided IDs');
      }

      return result.rows;
    });
  }

  async attachDocument(
    id: string,
    documentType: string,
    file: Express.Multer.File,
    metadata?: Record<string, any>
  ): Promise<Transaction> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `INSERT INTO transaction_documents (
           transaction_id,
           document_type,
           filename,
           mime_type,
           size,
           metadata
         ) VALUES ($1, $2, $3, $4, $5, $6)
         RETURNING *`,
        [
          id,
          documentType,
          file.originalname,
          file.mimetype,
          file.size,
          metadata
        ]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError('Failed to attach document to transaction');
      }

      return result.rows[0];
    });
  }

  async getDocuments(id: string): Promise<TransactionDocument[]> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `SELECT * FROM transaction_documents 
         WHERE transaction_id = $1
         ORDER BY created_at DESC`,
        [id]
      ) as DocumentQueryResult;

      return result.rows;
    });
  }

  async getAuditTrail(id: string): Promise<TransactionAuditLog[]> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `SELECT * FROM transaction_audit_log 
         WHERE transaction_id = $1
         ORDER BY created_at DESC`,
        [id]
      ) as AuditLogQueryResult;

      return result.rows;
    });
  }

  async search(conditions: string[], params: any[]): Promise<Transaction[]> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `SELECT * FROM transactions 
         WHERE ${conditions.join(' AND ')}
         ORDER BY created_at DESC`,
        params
      ) as TransactionQueryResult;

      return result.rows;
    });
  }

  async getFilterOptions(fiscalSponsorId: string): Promise<Record<string, string[]>> {
    return await this.withTransaction(async (client) => {
      const [statuses, types, categories] = await Promise.all([
        client.query(
          `SELECT DISTINCT status FROM transactions 
           WHERE fiscal_sponsor_id = $1`,
          [fiscalSponsorId]
        ) as Promise<FilterQueryResult>,
        
        client.query(
          `SELECT DISTINCT type FROM transactions 
           WHERE fiscal_sponsor_id = $1`,
          [fiscalSponsorId]
        ) as Promise<FilterQueryResult>,
        
        client.query(
          `SELECT DISTINCT category FROM transactions 
           WHERE fiscal_sponsor_id = $1`,
          [fiscalSponsorId]
        ) as Promise<FilterQueryResult>
      ]);

      return {
        statuses: statuses.rows.map(row => row.status).filter((status): status is string => status !== undefined),
        types: types.rows.map(row => row.type).filter((type): type is string => type !== undefined),
        categories: categories.rows.map(row => row.category).filter((category): category is string => category !== undefined)
      };
    });
  }

  async getTransactionSummary(
    fiscalSponsorId: string,
    filters?: Record<string, any>
  ): Promise<TransactionSummary> {
    const result = await this.withTransaction(async (client) => {
      return await client.query(
        `SELECT 
           COUNT(*) as total_count,
           SUM(amount) as total_amount,
           COUNT(CASE WHEN status = 'pending' THEN 1 END) as pending_count,
           SUM(CASE WHEN status = 'pending' THEN amount ELSE 0 END) as pending_amount
         FROM transactions 
         WHERE fiscal_sponsor_id = $1`,
        [fiscalSponsorId]
      ) as SummaryQueryResult;
    });

    if (!result.rows[0]) {
      throw new DatabaseError('Failed to get transaction summary');
    }

    return result.rows[0];
  }

  async getTransactionTrends(
    fiscalSponsorId: string,
    params?: Record<string, any>
  ): Promise<TransactionTrends> {
    const result = await this.withTransaction(async (client) => {
      return await client.query(
        `SELECT 
           DATE_TRUNC('month', date) as month,
           COUNT(*) as count,
           SUM(amount) as amount
         FROM transactions 
         WHERE fiscal_sponsor_id = $1
         GROUP BY DATE_TRUNC('month', date)
         ORDER BY month DESC
         LIMIT 12`,
        [fiscalSponsorId]
      ) as TrendsQueryResult;
    });

    return {
      monthly: result.rows
    };
  }

  async reconcile(
    id: string,
    bankTransactionId: string,
    matchType: 'exact' | 'manual' | 'split',
    notes?: string
  ): Promise<Transaction> {
    return await this.withTransaction(async (client) => {
      const result = await client.query(
        `UPDATE transactions 
         SET bank_transaction_id = $2,
             match_type = $3,
             reconciliation_notes = $4,
             reconciled_at = NOW()
         WHERE id = $1
         RETURNING *`,
        [id, bankTransactionId, matchType, notes]
      ) as TransactionQueryResult;

      if (!result.rows[0]) {
        throw new DatabaseError(`Transaction with id ${id} not found`);
      }

      return result.rows[0];
    });
  }

  static async createRecurring(data: Partial<IJournalEntry> & { recurringOptions: RecurringEntryOptions }): Promise<ITransaction> {
    try {
      const response = await apiInstance.post('/transactions', {
        ...data,
        type: 'RECURRING_JOURNAL_ENTRY',
        status: 'pending',
        metadata: {
          ...data.metadata,
          entryType: 'recurring',
          recurringOptions: data.recurringOptions,
          lineItems: data.lineItems
        }
      });
      return this.mapTransaction(response.data.data);
    } catch (error) {
      console.error('Error creating recurring journal entry:', error);
      throw error;
    }
  }

  static async listWithFilters(fiscalSponsorId: string, filters: JournalEntryFilters): Promise<ITransaction[]> {
    try {
      const response: AxiosResponse<ApiResponse<ITransaction[]>> = await apiInstance.get(`/transactions/${fiscalSponsorId}`);
      const filtered = response.data.data.filter((transaction: ITransaction) => {
        // Apply filters here
        if (filters.type && transaction.type !== filters.type) return false;
        if (filters.category && transaction.metadata?.category !== filters.category) return false;
        if (filters.dateRange) {
          const date = new Date(transaction.date);
          if (date < filters.dateRange.start || date > filters.dateRange.end) return false;
        }
        // ... add more filter conditions as needed
        return true;
      });
      return filtered.map(this.mapTransaction);
    } catch (error) {
      console.error('Error fetching filtered transactions:', error);
      throw error;
    }
  }

  static async reverse(
    transactionId: string,
    date: string,
    reason: string,
    notes?: string
  ): Promise<ITransaction> {
    try {
      const response: AxiosResponse<ApiResponse<ITransaction>> = await apiInstance.post(`/transactions/${transactionId}/reverse`, {
        date,
        reason,
        notes
      });
      return response.data.data;
    } catch (error) {
      throw new Error(`Failed to reverse transaction: ${error}`);
    }
  }

  static async search(
    fiscalSponsorIds: string[],
    params: {
      type?: string;
      format?: string;
      [key: string]: any;
    }
  ): Promise<ITransaction[]> {
    try {
      const response: AxiosResponse<ApiResponse<ITransaction[]>> = await apiInstance.post('/transactions/search', {
        fiscalSponsorIds,
        ...params
      });
      return response.data.data;
    } catch (error) {
      throw new Error(`Failed to search transactions: ${error}`);
    }
  }

  async getTransactions(filters?: any): Promise<{ data: Transaction[] }> {
    try {
      const query = `
        SELECT * FROM sponsfi.transactions
        WHERE ($1::jsonb IS NULL OR data @> $1::jsonb)
        ORDER BY created_at DESC
      `;
      
      const result = await this.executeQuery(
        query, 
        [filters ? JSON.stringify(filters) : null]
      );
      
      return { data: result.rows || [] };
    } catch (error) {
      console.error('Error getting transactions:', error);
      throw error;
    }
  }

  async getTransactionById(id: string): Promise<Transaction | null> {
    try {
      const query = `
        SELECT * FROM sponsfi.transactions
        WHERE id = $1
      `;
      
      const result = await this.executeQuery(query, [id]);
      
      return result.rows?.[0] || null;
    } catch (error) {
      console.error('Error getting transaction by ID:', error);
      throw error;
    }
  }

  async createTransaction(data: Partial<Transaction>): Promise<Transaction | null> {
    try {
      const fields = Object.keys(data).filter(key => data[key as keyof typeof data] !== undefined);
      const values = fields.map(field => data[field as keyof typeof data]);
      const placeholders = fields.map((_, i) => `$${i + 1}`).join(', ');
      
      const query = `
        INSERT INTO transactions (${fields.join(', ')})
        VALUES (${placeholders})
        RETURNING *
      `;
      
      const result = await this.executeQuery(query, values);
      
      return result.rows?.[0] || null;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  }

  async updateTransaction(id: string, data: Partial<Transaction>): Promise<Transaction | null> {
    try {
      const fields = Object.keys(data).filter(key => data[key as keyof typeof data] !== undefined);
      const values = fields.map(field => data[field as keyof typeof data]);
      const setClause = fields.map((field, i) => `${field} = $${i + 2}`).join(', ');
      
      const query = `
        UPDATE transactions
        SET ${setClause}
        WHERE id = $1
        RETURNING *
      `;
      
      const result = await this.executeQuery(query, [id, ...values]);
      
      return result.rows?.[0] || null;
    } catch (error) {
      console.error(`Error updating transaction ${id}:`, error);
      throw error;
    }
  }

  async deleteTransaction(id: string): Promise<boolean> {
    try {
      const query = `
        DELETE FROM transactions
        WHERE id = $1
      `;
      
      await this.executeQuery(query, [id]);
      
      return true;
    } catch (error) {
      console.error(`Error deleting transaction ${id}:`, error);
      throw error;
    }
  }
}

export default TransactionService; 